// --- Section factories: build the flexible page structure for each project type ---
const textSection = (id, eyebrow, heading, copy = null, decorated = false, extra = {}) => ({
    id, type: "text", eyebrow, heading, copy, decorated, ...extra,
});

const textImagesSection = (id, eyebrow, heading, copy = null, images = [], decorated = false, extra = {}) => ({
    id, type: "text-images", eyebrow, heading, copy, images, decorated, ...extra,
});

const imagesSection = (id, eyebrow, heading, images = [], boxed = false, decorated = false) => ({
    id, type: "images", eyebrow, heading, images, boxed, decorated,
});

const gallerySection = (id, eyebrow, heading, images = [], decorated = false) => ({
    id, type: "gallery", eyebrow, heading, images, decorated,
});

const moodboardSection = (id, eyebrow, heading, image = null, description = null, decorated = false) => ({
    id, type: "moodboard", eyebrow, heading, image, description, decorated,
});

const styleGuideSection = (id, eyebrow, heading, items = null, decorated = false) => ({
    id,
    type: "styleguide",
    eyebrow,
    heading,
    items: items || [
        { label: "Color Palette", image: null },
        { label: "Typography", image: null },
        { label: "Components", image: null },
        { label: "Accessibility / Color Usage", image: null },
    ],
    decorated,
});

// Product / UX projects: Overview → Objective → Research → Solutions/Ideas → Wireframes → Style Guide → Final Design → Outcome
export const productSections = (o = {}) => [
    ...(o.includeOverview === false ? [] : [textSection("overview", "Overview", "Overview", o.overview)]),
    textSection("objective", "Objective", "The Objective", o.objective),
    textImagesSection("research", "Discovery", "Research", o.researchCopy, o.researchImages || [], false, {
        subheading: o.researchSubheading,
        listLabel: o.researchListLabel,
        list: o.researchList,
        quote: o.researchQuote,
    }),
    textSection("solutions", "Ideation", "Solutions & Ideas", o.solutions, false, {
        subheading: o.solutionsSubheading,
        items: o.solutionsItems,
        quote: o.solutionsQuote,
        quoteLabel: o.solutionsQuoteLabel,
    }),
    imagesSection("wireframes", "Structure", "Wireframes", o.wireframes || [], true, true),
    styleGuideSection("styleguide", "Foundations", "Style Guide", o.styleGuide),
    gallerySection("final", "The Outcome", "Final Design", o.finalDesign || [], true),
    textSection("outcome", "Impact", "Outcome", o.outcome),
];

// Concept projects: Overview → Opportunity → Research → Concept/Idea → Experience/Solution → Final Design → Takeaway
export const conceptSections = (o = {}) => [
    textSection("overview", "Overview", "Overview", o.overview),
    textSection("opportunity", "The Problem", "Opportunity", o.opportunity),
    textImagesSection("research", "Discovery", "Research", o.researchCopy, o.researchImages || []),
    textSection("concept", "Ideation", "Concept & Idea", o.concept),
    textImagesSection("experience", "The Solution", "Experience & Solution", o.experienceCopy, o.experienceImages || [], true),
    gallerySection("final", "The Outcome", "Final Design", o.finalDesign || []),
    textSection("takeaway", "Reflection", "Takeaway", o.takeaway),
];

// Creative projects: Overview → Creative Brief → Research/Inspiration → Visual Direction → Design Exploration → Final Creatives → Takeaway
export const creativeSections = (o = {}) => [
    textSection("overview", "Overview", "Overview", o.overview),
    textSection("brief", "The Brief", "Creative Brief", o.brief),
    textImagesSection("research", "Discovery", "Research & Inspiration", o.researchCopy, o.researchImages || []),
    textImagesSection("direction", "Direction", "Visual Direction", o.directionCopy, o.directionImages || [], true),
    imagesSection("exploration", "Exploration", "Design Exploration", o.exploration || [], true),
    gallerySection("final", "The Outcome", "Final Creatives", o.finalDesign || [], true),
    textSection("takeaway", "Reflection", "Takeaway", o.takeaway),
];

const buildMeta = (role, platform, deliverables) => [
    { label: "Client", value: "—" },
    { label: "Role", value: role },
    { label: "Timeline", value: "—" },
    { label: "Tools", value: "Figma" },
    { label: "Platform", value: platform },
    { label: "Deliverables", value: deliverables },
];

const PRODUCT_DELIVERABLES = "Research · Wireframes · UI Design · Prototype";
const CONCEPT_DELIVERABLES = "Research · Concept · Experience Design";
const CREATIVE_DELIVERABLES = "Creative Brief · Visual Direction · Final Creatives";

export const projects = [
    {
        slug: "rajnigandha", title: "Rajnigandha", format: "Mobile App", industry: "E-commerce",
        desc: "A mobile commerce experience built around brand storytelling and effortless everyday journeys.",
        bg: "bg-gradient-to-br from-lav-100 via-lav-200 to-viol-light/40", cover: "/assets/covers/rajnigandha.jpg",
        page: {
            heading: "Rajnigandha",
            banner: "/assets/covers/rajnigandha.jpg",
            meta: [
                { label: "Client", value: "Rajnigandha" },
                { label: "Role", value: "UI/UX Designer" },
                { label: "Timeline", value: "1 Week" },
                { label: "Tools", value: "Figma" },
                { label: "Platform", value: "Mobile App" },
                { label: "Type", value: "Digital Pitch" },
            ],
            sections: [
                textSection("objective", "Objective", "The Objective",
                    "Redesign and modernize the Rajnigandha app to increase engagement, support bulk ordering and create a stronger rewards experience."
                ),
                textImagesSection("research", "Discovery", "Research",
                    "Explored the existing Rajnigandha experience and studied loyalty, rewards and engagement patterns to identify opportunities for stronger customer interaction and repeat purchases.",
                    [],
                    false,
                    {
                        listLabel: "Focus",
                        list: ["Loyalty", "Rewards", "Redemption", "Engagement", "Repeat Purchases"],
                        quote: "A well-integrated rewards experience can give customers more reasons to return, interact and purchase through the app.",
                    }
                ),
                textSection("solutions", "Ideation", "Solutions & Ideas", null, false, {
                    items: [
                        { num: "01", title: "Smarter product discovery", desc: "" },
                        { num: "02", title: "Convenient bulk ordering", desc: "" },
                        { num: "03", title: "Stronger rewards and loyalty", desc: "" },
                        { num: "04", title: "Better customer engagement", desc: "" },
                        { num: "05", title: "Premium modern UI", desc: "" },
                    ],
                }),
                imagesSection("wireframes", "Structure", "Wireframes", [
                    { src: "/assets/projects/rajnigandha/wireframe-1.png", caption: "Home & Product Range — low-fidelity structure" },
                    { src: "/assets/projects/rajnigandha/wireframe-2.png", caption: "Logged-out state & navigation — low-fidelity structure" },
                    { src: "/assets/projects/rajnigandha/wireframe-3.png", caption: "Points tracking & rewards — low-fidelity structure" },
                ], true, true),
                moodboardSection(
                    "moodboard",
                    "Visual Direction",
                    "Moodboard",
                    "/assets/projects/rajnigandha/moodboard.png",
                    "Exploring a premium visual direction inspired by refined hospitality, elegance and celebration, using rich greens, warm neutrals, gold accents and sophisticated imagery to bring the Royal Refreshment concept to life."
                ),
                styleGuideSection("styleguide", "Foundations", "Style Guide", [
                    { label: "Color Palette", image: "/assets/projects/rajnigandha/style-color-palette.png" },
                    { label: "Typography", image: "/assets/projects/rajnigandha/style-typography.png" },
                    { label: "Components", image: null },
                    { label: "Accessibility / Color Usage", image: null },
                ]),
                gallerySection("final", "The Outcome", "Final Design", [
                    { src: "/assets/projects/rajnigandha/final-1.png", caption: "Non-logged in user — product range, offers and rewards discovery" },
                    { src: "/assets/projects/rajnigandha/final-2.png", caption: "Logged in user — personalised home, subscriptions and product range" },
                    { src: "/assets/projects/rajnigandha/final-3.png", caption: "Rewards — points tracking, redemption and leaderboard" },
                ]),
                textSection("outcome", "Impact", "Outcome"),
            ],
        },
    },
    {
        slug: "smaaash", title: "Smaaash", format: "Website Design", industry: "Entertainment",
        desc: "A high-energy website experience for India's leading gaming and entertainment destination.",
        bg: "bg-gradient-to-br from-white via-lav-100 to-sun-soft/50", cover: "/assets/covers/smaaash.jpg",
        page: {
            heading: "Smaaash",
            banner: "/assets/covers/smaaash.jpg",
            liveUrl: "https://smaaash.in/",
            meta: [
                { label: "Client", value: "Smaaash Entertainment" },
                { label: "Role", value: "UI/UX Designer" },
                { label: "Timeline", value: "2 Months" },
                { label: "Tools", value: "Figma" },
                { label: "Platform", value: "Website" },
                { label: "Type", value: "Website Revamp" },
            ],
            sections: [
                textSection("objective", "Objective", "The Objective",
                    "Redesign the Smaaash website with a more modern, engaging visual experience that helps customers easily explore Smaaash experiences, discover games and events, and complete bookings with less friction."
                ),
                textImagesSection("research", "Discovery", "Understanding the Existing Experience",
                    "I started by exploring the existing Smaaash website to understand how people discover games, experiences, events and centres, and how easily they can move from exploring to booking.",
                    [
                        { src: "/assets/projects/smaaash/research-audit.webp", caption: "Existing Website Audit — key UX opportunities identified" },
                    ],
                    false,
                    {
                        listLabel: "Existing Website — Opportunities",
                        list: [
                            "Too many competing entry points",
                            "Discovery can be clearer",
                            "Booking needs more visibility",
                            "Content feels heavy",
                            "Different user needs need clearer paths",
                            "Visual experience can be more engaging",
                        ],
                        quote: "Create a clearer journey that helps users Discover → Explore → Choose → Book.",
                        quoteLabel: "Key UX Opportunity",
                        secondBlock: {
                            label: "Competitor Analysis",
                            copy: "I also looked at other entertainment experiences to understand how they approach discovery, visual communication, activities and booking. This helped identify opportunities to make the Smaaash experience clearer, more engaging and easier to navigate.",
                            image: "/assets/projects/smaaash/research-competitor.png",
                            caption: "Competitor Analysis — Timezone, Game Palacio, Funcity and Dave & Buster’s",
                        },
                    }
                ),
                textSection("solutions", "Ideation", "Solutions & Ideas", null, false, {
                    items: [
                        { num: "01", title: "Modern and engaging visual design", desc: "" },
                        { num: "02", title: "Easier exploration of games, experiences and events", desc: "" },
                        { num: "03", title: "Clearer information hierarchy and navigation", desc: "" },
                        { num: "04", title: "Simplified booking journey", desc: "" },
                        { num: "05", title: "Stronger presentation of offers and experiences", desc: "" },
                        { num: "06", title: "More engaging content and interactions", desc: "" },
                        { num: "07", title: "Mobile-friendly experience", desc: "" },
                    ],
                }),
                imagesSection("wireframes", "Structure", "Wireframes", [
                    { src: "/assets/projects/smaaash/wireframe-1.png", caption: "Home Page — low-fidelity structure" },
                    { src: "/assets/projects/smaaash/wireframe-2.png", caption: "Master Party Page — low-fidelity structure" },
                    { src: "/assets/projects/smaaash/wireframe-3.png", caption: "Thrill Zone Page — low-fidelity structure" },
                ], true, true),
                gallerySection("final", "The Outcome", "Final Design", [
                    { src: "/assets/projects/smaaash/final-1.png", caption: "Parties & Events — Girls Night, bookings and immersive experiences" },
                    { src: "/assets/projects/smaaash/final-2.png", caption: "Thrill Zone — Go Karting, Bowling, VR and more" },
                    { src: "/assets/projects/smaaash/final-3.png", caption: "OG Bowling Zone, Gallery, Testimonials and Contact" },
                ]),
                textSection("outcome", "Impact", "Outcome"),
            ],
        },
    },
    {
        slug: "axis-atlas-credit-card", title: "Axis Atlas Credit Card", format: "Website Design", industry: "Finance / Banking",
        desc: "A premium travel rewards credit card landing page designed for a high-net-worth audience.",
        bg: "bg-gradient-to-br from-lav-50 via-white to-lav-200", cover: "/assets/covers/axis-atlas.jpg",
        page: {
            heading: "Axis Atlas Credit Card",
            banner: "/assets/covers/axis-atlas.jpg",
            liveUrl: "https://www.axis.bank.in/cards/credit-card/axis-bank-atlas-credit-card",
            meta: [
                { label: "Client", value: "Axis Bank" },
                { label: "Role", value: "UI/UX Designer" },
                { label: "Timeline", value: "2 weeks" },
                { label: "Tools", value: "Figma" },
                { label: "Platform", value: "Website" },
                { label: "Type", value: "Credit Card / Financial Services" },
                { label: "Deliverables", value: PRODUCT_DELIVERABLES },
            ],
            sections: [
                textSection("overview", "Overview", "Overview",
                    "Axis Atlas is a premium travel rewards credit card designed for high-net-worth customers. It offers EDGE Miles that can be redeemed for flights, hotels and partner airline and hotel loyalty programs, along with accelerated travel rewards and tiered milestone benefits.",
                    false,
                    {
                        extraParagraphs: [
                            "The brief was to create a simple, premium landing page that clearly communicates the card’s benefits, rewards, privileges, tier benefits and eligibility information.",
                            "I worked across both UX and UI design, starting with wireframes based on the client brief and provided content. I organised the information into a clear hierarchy and used tabs to make detailed information such as Special Features, Rewards, Privileges, Tier Benefits and Eligibility easier to explore.",
                            "For the visual direction, I created a dark and premium style suited to the high-net-worth audience. I used Axis Bank’s primary burgundy colour and introduced subtle travel-inspired elements such as globe graphics, dotted routes and connected lines. I also worked with reusable UI components and the design system to maintain consistency across the experience.",
                        ],
                    }
                ),
                textImagesSection("research", "Discovery", "Research",
                    "I reviewed the client brief and product information to understand the Atlas card, its premium positioning and the different reward benefits that needed to be communicated.",
                    [],
                    false,
                    {
                        listLabel: "Key areas explored",
                        list: [
                            "Information hierarchy",
                            "Reward and tier structure",
                            "Content organisation",
                            "Premium audience expectations",
                            "Easy access to detailed information",
                        ],
                        quote: "The card contains multiple layers of information including travel rewards, EDGE Miles, accelerated travel benefits, milestone tiers, privileges and eligibility. I focused on organising this information into a clear hierarchy without making the landing page feel overwhelming.",
                    }
                ),
                imagesSection("wireframes", "Structure", "Wireframes", [
                    { src: "/assets/projects/axis-atlas/wireframe-1.png", caption: "Low-fidelity wireframes establishing content structure and hierarchy" },
                ], true, true),
                gallerySection("final", "The Outcome", "Final Design", [
                    { src: "/assets/projects/axis-atlas/final-1.png", caption: "Atlas Credit Card — final dark, premium landing page design" },
                ]),
            ],
        },
    },
    {
        slug: "gsk", title: "GSK", format: "Website Design", industry: "Pharmaceutical",
        desc: "A pharmaceutical web presence balancing scientific credibility with human warmth.",
        bg: "bg-gradient-to-br from-lav-200 via-lav-100 to-white", cover: "/assets/covers/gsk.jpg",
        page: {
            heading: "GSK",
            banner: "/assets/covers/gsk.jpg",
            meta: [
                { label: "Client", value: "GSK" },
                { label: "Role", value: "UI Designer" },
                { label: "Tools", value: "Figma" },
                { label: "Platform", value: "Website" },
                { label: "Type", value: "Website Design" },
            ],
            sections: [
                textSection("overview", "Overview", "Overview",
                    "GSK is a global pharmaceutical and healthcare company with digital experiences created for healthcare professionals across different markets. I worked as a UI Designer across multiple GSK product experiences, adapting the global design system to different products while maintaining a consistent GSK brand experience.",
                    false,
                    {
                        products: [
                            {
                                name: "Polyfax",
                                location: "Pakistan",
                                description: "A product website for Polyfax cream, presenting medical and product information clearly for healthcare professionals.",
                            },
                            {
                                name: "Shingrix",
                                location: "Saudi Arabia",
                                description: "A medical resource hub for Shingrix, the shingles vaccine, designed to organise medical information, resources and supporting content.",
                            },
                            {
                                name: "Trelegy",
                                location: "Saudi Arabia",
                                description: "A product experience for Trelegy, presenting detailed medical and product information through a structured web interface.",
                            },
                        ],
                        roleLabel: "My Role",
                        roleCopy: "My work focused on UI design, design-system implementation and creating consistent digital experiences across different products and markets.",
                        roleCopy2: "I worked with GSK’s global component library and adapted existing components according to each product’s requirements. I also created and maintained product-specific style guides, including colour systems, typography, UI components and visual rules.",
                        roleCopy3: "Each product had its own colour direction and visual requirements, so I developed appropriate colour systems while ensuring they remained aligned with the overall GSK brand and design system.",
                        roleList: [
                            "UI layouts and responsive web screens",
                            "Medical data and content presentation",
                            "Information hierarchy",
                            "Product-specific colour systems",
                            "Component usage and adaptation",
                            "Style guides and design-system documentation",
                            "Maintaining consistency across different markets and products",
                        ],
                        ctas: [
                            { label: "View Polyfax Design", url: "https://www.figma.com/proto/kkOGhKOoKbrnCNpGvG5v8h/sushma-s-work-VML?node-id=7-273892&t=Q75XpCGyVJFx7JzK-8&scaling=scale-down-width&content-scaling=fixed&page-id=0%3A1&starting-point-node-id=7%3A273892&show-proto-sidebar=1&hide-ui=1" },
                            { label: "View Shingrix Design", url: "https://www.figma.com/proto/kkOGhKOoKbrnCNpGvG5v8h/sushma-s-work-VML?node-id=7-45470&t=lfR05fTKhktmQpfQ-8&scaling=scale-down-width&content-scaling=fixed&page-id=0%3A1&starting-point-node-id=7%3A45470&hide-ui=1" },
                            { label: "View Trelegy Design", url: "https://www.figma.com/proto/kkOGhKOoKbrnCNpGvG5v8h/sushma-s-work-VML?node-id=7-281009&t=uygUC4Xh3KfmBtEe-8&scaling=scale-down-width&content-scaling=fixed&page-id=0%3A1&starting-point-node-id=7%3A281009&show-proto-sidebar=1&hide-ui=1" },
                        ],
                    }
                ),
                gallerySection("final", "The Work", "Final Design", [
                    { src: "/assets/projects/gsk/design-1.png", caption: "Trelegy — Saudi Arabia: once-daily triple therapy for asthma and COPD" },
                    { src: "/assets/projects/gsk/design-2.png", caption: "Polyfax — Pakistan: product information and clinical evidence" },
                    { src: "/assets/projects/gsk/design-3.png", caption: "Shingrix — Saudi Arabia: medical resource hub for the shingles vaccine" },
                    { src: "/assets/projects/gsk/design-4.png", caption: "Betnovate — UK: potent relief for steroid-responsive dermatoses" },
                    { src: "/assets/projects/gsk/design-5.png", caption: "Betnovate — UK: dosing guidelines, safety profile and references" },
                ]),
            ],
        },
    },
    {
        slug: "dr-reddys", title: "Dr. Reddy's", format: "Website Design", industry: "Healthcare / E-commerce",
        desc: "A digital healthcare platform bringing e-commerce, health tracking and diabetes education together.",
        bg: "bg-gradient-to-br from-lav-100 via-white to-sun-soft/40", cover: "/assets/covers/dr-reddys.jpg",
        page: {
            heading: "Dr. Reddy's",
            banner: "/assets/covers/dr-reddys.jpg",
            meta: [
                { label: "Client", value: "Dr. Reddy’s" },
                { label: "Role", value: "UI/UX Designer" },
                { label: "Timeline", value: "3 Months" },
                { label: "Tools", value: "Figma" },
                { label: "Platform", value: "Website" },
                { label: "Type", value: "Healthcare · E-commerce · Health Tracking" },
                { label: "Scope", value: "60+ Screens" },
            ],
            sections: [
                textSection("overview", "Overview", "Overview",
                    "This project focused on creating a digital healthcare experience for people managing diabetes, bringing e-commerce, personal health management and educational content together in one platform.",
                    false,
                    {
                        extraParagraphs: [
                            "I worked on the project from start to finish across UX and UI, covering more than 100 screens and multiple user journeys.",
                            "The experience was designed around three core needs: helping users purchase healthcare products, manage and track their health, and access useful diabetes-related information and lifestyle content.",
                        ],
                    }
                ),
                textImagesSection("research", "Discovery", "Research",
                    "I explored the needs of people managing diabetes and how they interact with healthcare products, health information and everyday wellness content.",
                    [],
                    false,
                    {
                        listLabel: "The research focused on",
                        list: [
                            "Healthcare product discovery",
                            "E-commerce and purchase journey",
                            "Personal health tracking",
                            "Diabetes management",
                            "Nutrition and food preferences",
                            "Exercise and fitness",
                            "Educational content",
                            "Long-term user engagement",
                        ],
                        quoteLabel: "Key Insight",
                        quote: "Managing diabetes involves more than purchasing healthcare products. Users also need a simple way to monitor their health, set personal goals and access relevant information that supports their everyday lifestyle.",
                    }
                ),
                textSection("flow-01", "Core Flow 01", "E-Commerce",
                    "A dedicated shopping experience where users can discover healthcare products, explore product information and complete their purchase journey.",
                    false,
                    {
                        extraParagraphs: [
                            "The flow was designed to make product discovery and purchasing straightforward while keeping relevant healthcare information accessible throughout the journey.",
                        ],
                        ctas: [
                            { label: "View E-commerce Flow", url: "https://www.figma.com/proto/r0tuAVz8MDhS8G4142iauM/Dr-reddys-new?node-id=1953-4023&t=eRrm3JJmW7GwnKW8-0&scaling=scale-down&content-scaling=fixed&starting-point-node-id=1953%3A4023&show-proto-sidebar=1" },
                        ],
                    }
                ),
                textSection("flow-02", "Core Flow 02", "Digital Diary",
                    "A personal health space where users can track their diabetes and overall wellness.",
                    false,
                    {
                        extraParagraphs: [
                            "Users can record their health information before or after meals, set target blood sugar levels, define food preferences and fitness goals, and keep track of their progress.",
                            "The experience also allows users to connect with a nutritionist, making the digital diary more than just a tracking tool and helping users manage their health more actively.",
                        ],
                        ctas: [
                            { label: "View Digital Diary Flow", url: "https://www.figma.com/proto/r0tuAVz8MDhS8G4142iauM/Dr-reddys-new?node-id=7298-9793&t=eRrm3JJmW7GwnKW8-0&scaling=scale-down&content-scaling=fixed&starting-point-node-id=7298%3A9793&show-proto-sidebar=1" },
                        ],
                    }
                ),
                textSection("flow-03", "Core Flow 03", "Content & Engagement",
                    "A dedicated content experience designed to help users learn more about diabetes and build healthier everyday habits.",
                    false,
                    {
                        extraParagraphs: [
                            "Users can explore diabetes education, healthy recipes, exercise and lifestyle content. The experience also includes quizzes and a rewards section to make learning more engaging and encourage continued interaction.",
                        ],
                    }
                ),
                imagesSection("wireframes", "Structure", "Wireframes", [
                    { src: "/assets/projects/dr-reddys/wireframe-1.png", caption: "Low-fidelity wireframes — shop, product, bundles and nutritionist screens" },
                ], true, true),
                gallerySection("final", "The Outcome", "Final Design", [
                    { src: "/assets/projects/dr-reddys/final-1.png", caption: "E-Commerce — product discovery, bundles and nutritionist panel" },
                    { src: "/assets/projects/dr-reddys/final-2.png", caption: "Home & Shop — bestsellers, expert content and value bundles" },
                    { src: "/assets/projects/dr-reddys/final-3.png", caption: "Digital Diary — onboarding, goals, BMI and food preferences" },
                    { src: "/assets/projects/dr-reddys/final-4.png", caption: "Content & Engagement — blogs, recipes, quizzes and health dashboard" },
                ]),
            ],
        },
    },
    {
        slug: "portfolio-tracking-dashboard",
        title: "Portfolio Tracking Dashboard",
        format: "Web Application",
        industry: "SaaS",
        desc: "A data-dense SaaS dashboard that makes portfolio insights readable at a glance.",
        bg: "bg-gradient-to-br from-viol-light/30 via-lav-100 to-white",
        cover: "/assets/covers/portfolio-tracking-dashboard.jpg",
        page: {
            heading: "Portfolio Tracking for Sequoia Capital",
            banner: "/assets/covers/portfolio-tracking-dashboard.jpg",
            meta: [
                { label: "Client", value: "Sequoia Capital" },
                { label: "Role", value: "Product Designer" },
                { label: "Timeline", value: "3 Weeks" },
                { label: "Tools", value: "Figma" },
                { label: "Platform", value: "Web Application / Dashboard" },
                { label: "Deliverables", value: PRODUCT_DELIVERABLES },
            ],
            sections: productSections({
                includeOverview: false,
                objective:
                    "The objective was to design a centralized portfolio tracking dashboard that simplifies complex investment data and enables users to monitor portfolio companies, performance and key financial metrics in one clear and structured experience.",
                researchSubheading: "Understanding complex portfolio workflows",
                researchCopy:
                    "I explored existing investment and portfolio-management platforms to understand how financial information is structured, presented, and navigated. The research focused on identifying useful dashboard patterns, organizing complex metrics into a clear hierarchy, and finding effective ways to help users scan and compare portfolio information.",
                researchListLabel: "Research Focus",
                researchList: [
                    "Portfolio & investment platforms",
                    "Dashboard and navigation patterns",
                    "Information hierarchy",
                    "Financial data visualization",
                ],
                researchQuote: "Users need a clear overview first, with deeper portfolio and company-level information available when needed.",
                researchImages: [
                    { src: "/assets/projects/portfolio-tracking/research-ia.png", caption: "Information architecture — dashboard navigation structure" },
                    { src: "/assets/projects/portfolio-tracking/research-persona.png", caption: "User persona — Amit Sharma, Investment Analyst" },
                    { src: "/assets/projects/portfolio-tracking/research-userflow.png", caption: "User flow — from login to performance insights" },
                ],
                solutionsSubheading: "Turning insights into a focused portfolio experience",
                solutions:
                    "Based on the research and exploration, I focused on creating a dashboard that balances clarity, depth, and flexibility.",
                solutionsItems: [
                    { num: "01", title: "Portfolio at a Glance", desc: "Bring key portfolio performance and metrics together so users can understand the overall picture quickly." },
                    { num: "02", title: "Structured Information", desc: "Organize portfolio, company, and financial information into clear levels, making complex data easier to navigate." },
                    { num: "03", title: "Visual Performance Tracking", desc: "Use charts, metrics, and visual indicators to make trends and performance easier to scan and compare." },
                    { num: "04", title: "Flexible Exploration", desc: "Use filters and structured navigation to help users quickly find specific companies, metrics, or portfolio information." },
                ],
                solutionsQuoteLabel: "Design Principle",
                solutionsQuote: "Show what matters first. Let users explore deeper when they need to.",
                wireframes: [
                    { src: "/assets/projects/portfolio-tracking/wireframe-1.png", caption: "Dashboard overview — low-fidelity structure" },
                    { src: "/assets/projects/portfolio-tracking/wireframe-2.png", caption: "Company detail — low-fidelity structure" },
                ],
                styleGuide: [
                    { label: "Color Palette", image: "/assets/projects/portfolio-tracking/style-color-palette.png" },
                    { label: "Typography", image: "/assets/projects/portfolio-tracking/style-typography.png" },
                    { label: "Components", image: null },
                    { label: "Accessibility / Color Usage", image: "/assets/projects/portfolio-tracking/style-accessibility.png" },
                ],
                finalDesign: [
                    { src: "/assets/projects/portfolio-tracking/final-1.jpg", caption: "Dashboard overview — portfolio value, growth and diversification" },
                    { src: "/assets/projects/portfolio-tracking/final-2.jpg", caption: "Company detail — financials, funding and key metrics" },
                    { src: "/assets/projects/portfolio-tracking/final-3.jpg", caption: "Portfolio listing — organizations, valuation and performance" },
                ],
            }),
        },
    },
    { slug: "apca", title: "APCA", format: "Website Design", industry: "", desc: "A clean institutional website designed with structured content and clear hierarchy.", bg: "bg-gradient-to-br from-white via-lav-200 to-lav-100" },
    { slug: "hdfc", title: "HDFC", format: "Website Design", industry: "Banking", desc: "A banking web experience designed for trust, findability and everyday tasks.", bg: "bg-gradient-to-br from-lav-100 via-lav-50 to-viol-light/30" },
    {
        slug: "starbucks", title: "Starbucks", format: "UX / Product Concept", industry: "Food & Beverage",
        desc: "A research-driven concept exploring loyalty, personalisation and discovery beyond ordering.",
        bg: "bg-gradient-to-br from-lav-50 via-lav-100 to-sun-soft/40", cover: "/assets/covers/starbucks.jpg",
        page: {
            heading: "Starbucks",
            banner: "/assets/covers/starbucks.jpg",
            meta: [
                { label: "Client", value: "Starbucks" },
                { label: "Role", value: "UI/UX Designer" },
                { label: "Timeline", value: "1 week" },
                { label: "Tools", value: "Figma" },
                { label: "Platform", value: "Mobile App" },
                { label: "Type", value: "UX Research & Concept Design" },
                { label: "Deliverables", value: "Research · Concept Design · UI Design" },
            ],
            sections: [
                textSection("overview", "Overview", "Overview",
                    "This project explored opportunities to make the Starbucks app experience more engaging beyond ordering, with a focus on loyalty, personalisation, discovery and customer engagement.",
                    false,
                    {
                        extraParagraphs: [
                            "I explored the existing app experience, customer journey and loyalty experience to identify opportunities where the digital experience could create stronger reasons for customers to discover, interact and return to Starbucks.",
                        ],
                    }
                ),
                textImagesSection("research", "Discovery", "Research",
                    "I explored the existing Starbucks app experience across ordering, rewards, personalisation and customer engagement. I also looked at the relationship between the digital experience and the in-store experience to identify opportunities for deeper engagement.",
                    [
                        { src: "/assets/projects/starbucks/research-third-place.webp", caption: "Starbucks' \u201cThird Place\u201d philosophy and why it matters" },
                        { src: "/assets/projects/starbucks/research-instore-cx.webp", caption: "In-store CX — customer journey, systems thinking and experience framework" },
                        { src: "/assets/projects/starbucks/research-user-flows.webp", caption: "Existing app user flows — login/signup, homepage, order and loyalty program" },
                        { src: "/assets/projects/starbucks/research-compscan.webp", caption: "Competitor scan, SWOT analysis and key strategic learnings" },
                    ],
                    false,
                    {
                        listLabel: "Research focus",
                        list: [
                            "Loyalty & Rewards",
                            "Personalisation",
                            "Product Discovery",
                            "Customer Engagement",
                            "Repeat Visits",
                            "Digital + In-store Experience",
                        ],
                        quoteLabel: "Key insight",
                        quote: "The opportunity was to make the app feel less transactional and create more reasons for customers to discover, interact and return.",
                    }
                ),
                textImagesSection("idea-01", "Idea 01", "Starbucks Passport",
                    "A digital passport that turns Starbucks visits into a journey of discovery. Customers collect stamps as they visit different stores and unlock rewards as they complete milestones.",
                    [
                        { src: "/assets/projects/starbucks/idea-01-passport.png", caption: "Starbucks Passport — collect stamps, unlock rewards" },
                    ],
                    true,
                    {
                        blocks: [
                            { label: "How It Works", text: "Customers receive a digital stamp after visiting participating Starbucks locations. Their passport gradually fills as they explore more stores, creating a visual sense of progress. Completing a set of stamps unlocks exclusive rewards." },
                            { label: "Why I Explored This Idea", text: "The existing loyalty experience is largely connected to earning rewards through purchases. I wanted to introduce a more experiential layer that encourages customers to visit, explore and collect, making the journey itself rewarding." },
                            { label: "Experience Opportunity", items: [
                                "Encourages exploration of different Starbucks locations",
                                "Creates a visible sense of progress",
                                "Adds gamification without making the experience complicated",
                                "Gives customers another reason to return",
                            ] },
                            { label: "Potential Impact", text: "The concept could increase store discovery and repeat visits while creating a stronger emotional connection with the Starbucks brand." },
                        ],
                    }
                ),
                textImagesSection("idea-02", "Idea 02", "Scan & Surprise",
                    "Turn every Starbucks cup into a small digital moment. Customers scan a QR code on their cup through the Starbucks app to unlock a surprise reward or experience.",
                    [
                        { src: "/assets/projects/starbucks/idea-02-scan-surprise.png", caption: "Scan & Surprise — a QR moment after every purchase" },
                    ],
                    false,
                    {
                        blocks: [
                            { label: "How It Works", text: "After purchasing a drink, customers scan the QR code on the cup. The app reveals a surprise such as a free drink upgrade, bonus Stars, an offer or another digital reward that can be redeemed directly through the app." },
                            { label: "Why I Explored This Idea", text: "The interaction does not have to end when the customer receives their drink. I explored how a physical touchpoint, the Starbucks cup, could connect back to the digital experience and create an unexpected moment after purchase." },
                            { label: "Experience Opportunity", items: [
                                "Connects physical and digital touchpoints",
                                "Creates anticipation after every purchase",
                                "Introduces an element of surprise into the loyalty experience",
                                "Encourages customers to open and interact with the app",
                            ] },
                            { label: "Potential Impact", text: "The concept could increase post-purchase engagement, repeat interactions and loyalty by making each purchase feel a little more rewarding." },
                        ],
                    }
                ),
                textImagesSection("idea-03", "Idea 03", "Taste Quest",
                    "An AI-powered conversational experience that helps customers discover a Starbucks drink based on their preferences, mood and taste.",
                    [
                        { src: "/assets/projects/starbucks/idea-03-taste-quest.webp", caption: "Taste Quest — AI Barista drink recommendations" },
                    ],
                    true,
                    {
                        blocks: [
                            { label: "How It Works", text: "Customers answer a few simple questions such as what they are in the mood for, whether they prefer hot or cold and their preferred caffeine level. Taste Quest then recommends a drink with relevant customisation options, which can be ordered or saved for later." },
                            { label: "Why I Explored This Idea", text: "Starbucks has a large and highly customisable menu, which can make discovering something new difficult. I explored how an AI-powered experience could make product discovery feel more personal and conversational instead of asking customers to browse through a long menu." },
                            { label: "Experience Opportunity", items: [
                                "Makes drink discovery easier",
                                "Helps customers discover products beyond their usual order",
                                "Uses preferences to create personalised recommendations",
                                "Creates a more conversational relationship with the brand",
                            ] },
                            { label: "Potential Impact", text: "The concept could improve product discovery and personalisation while encouraging customers to try new beverages and return to the app for future recommendations." },
                        ],
                    }
                ),
            ],
        },
    },
    {
        slug: "lakme-skin-persona-ai", title: "Lakmé Skin Persona AI", format: "UX / AI Concept", industry: "Beauty / AI",
        desc: "An AI-led concept that personalises skincare through the context and behaviour behind a routine.",
        bg: "bg-gradient-to-br from-lav-200 via-white to-lav-100", cover: "/assets/covers/lakme.jpg",
        page: {
            heading: "Lakmé Skin Persona AI",
            banner: "/assets/covers/lakme.jpg",
            meta: [
                { label: "Client", value: "Lakmé" },
                { label: "Role", value: "UI/UX Designer" },
                { label: "Timeline", value: "1 week" },
                { label: "Tools", value: "Figma" },
                { label: "Platform", value: "Digital Experience" },
                { label: "Type", value: "Concept Design · UX Research · AI Personalisation" },
            ],
            sections: [
                textSection("overview", "Overview", "Overview",
                    "This project explored how Lakmé could move beyond traditional skincare routines and create a more contextual, personalised experience.",
                    false,
                    {
                        extraParagraphs: [
                            "The exploration started with a broader question: instead of recommending skincare based only on skin type or a fixed AM/PM routine, what if the experience could understand the person, their lifestyle and the factors influencing their skin?",
                            "This led to the concept of Skin Math™ — an AI-powered approach that looks at the behaviour and context behind a user's skincare routine and uses those signals to create a more personalised experience.",
                        ],
                    }
                ),
                textImagesSection("orthodoxies", "Research", "Breaking the Orthodoxies in Skincare",
                    "I looked at how skincare personalisation is usually approached and challenged some of the common assumptions behind it. From this exploration, I identified four shifts:",
                    [
                        { src: "/assets/projects/lakme/breaking-orthodoxies.png", caption: "From industry default to the shift Skin Math™ could create" },
                    ],
                    true,
                    {
                        blocks: [
                            { label: "1. The Clock Dictates The Routine → Life Dictates The Routine", text: "Moments, energy and environment can influence what the skin needs." },
                            { label: "2. Products Are The Hero → Context Is The Hero", text: "Products become the response to the user's situation rather than the starting point." },
                            { label: "3. Personalisation = One-Time Quiz → Personalisation = Living, Learning Relationship", text: "A user's needs can change over time, so personalisation should evolve with them." },
                            { label: "4. Skincare Is A Solo, Silent Ritual → Skincare Can Be Coached, Social And Rooted In Everyday Life", text: "The experience can support users beyond simply recommending products." },
                        ],
                    }
                ),
                textImagesSection("what-if", "Exploration", "What If...",
                    "I used “What if” questions to push the idea beyond a conventional skincare recommendation tool and explore how AI could become more aware of the user's everyday context.",
                    [
                        { src: "/assets/projects/lakme/what-ifs.png", caption: "What if... — questions that shaped the direction of Skin Math™" },
                    ],
                    false
                ),
                textImagesSection("behaviour", "Behavioural Design", "The Behaviour Behind the Routine",
                    "The exploration moved from understanding the product to understanding the person behind the routine. The concept was built around five behavioural principles:",
                    [
                        { src: "/assets/projects/lakme/behavior-behind-routine.png", caption: "The five behavioural principles behind Skin Math™" },
                    ],
                    true,
                    {
                        blocks: [
                            { label: "Habit Stacking", text: "Build around habits users already have." },
                            { label: "Operational Transparency", text: "Show how the recommendation is being created." },
                            { label: "The Empathic Forgiver", text: "Support users on the days when routines are difficult to maintain." },
                            { label: "The Skin Twin Effect", text: "Make users feel personally understood." },
                            { label: "The Instant Gratifier", text: "Give users a sense of progress and reward along the journey." },
                        ],
                    }
                ),
                textImagesSection("skin-mirror", "Concept — Skin Math™", "Skin Mirror",
                    "Skin Math™ is an AI-powered skincare experience that looks beyond basic skin type and considers lifestyle, profession, hormones, daily habits and environmental factors to create a personalised “Skin Persona.” The idea is that skin is dynamic — what a person needs can change depending on what is happening in their life.",
                    [
                        { src: "/assets/projects/lakme/skin-mirror.png", caption: "Skin Mirror — the expression of the Skin Math™ concept" },
                    ],
                    false,
                    {
                        extraParagraphs: [
                            "Skin Mirror is the expression of the Skin Math™ concept. It uses AI to analyse lifestyle, profession, hormones and daily habits to create a unique Skin Persona and build an adaptive skincare routine that can evolve with the user. The experience can identify different contexts and concerns and translate them into a personalised skincare journey.",
                        ],
                    }
                ),
                textImagesSection("execution", "Execution", "Skin Persona",
                    "The experience can begin through a QR code on a Lakmé product pack, creating a direct bridge between the physical product and the digital experience. The user enters an AI consultation where signals such as facial appearance, lifestyle, hormones and daily habits are considered. The system then builds a Skin Persona and identifies relevant concerns, creating a more contextual and personalised routine.",
                    [
                        { src: "/assets/projects/lakme/execution.png", caption: "Skin Persona — Skin Health Score and personalised routine" },
                    ],
                    true,
                    {
                        listLabel: "Key Stages",
                        list: [
                            "QR Scan",
                            "AI Consultation",
                            "Context & Concern Detection",
                            "Skin Persona",
                            "Skin Health Score",
                            "Personalised Routine",
                        ],
                    }
                ),
            ],
        },
    },
    {
        slug: "ceat", title: "CEAT", format: "Banners / Graphic Design", industry: "Automotive",
        desc: "A bold campaign banner system crafted for consistency across digital touchpoints.",
        bg: "bg-gradient-to-br from-white via-lav-100 to-lav-200", cover: "/assets/covers/ceat.jpg",
        page: {
            heading: "CEAT",
            banner: "/assets/covers/ceat.jpg",
            meta: buildMeta("Visual / Graphic Designer", "Banners / Graphic Design", CREATIVE_DELIVERABLES),
            sections: creativeSections(),
        },
    },
    {
        slug: "bandhan-bank", title: "Bandhan Bank", format: "Emailer Design", industry: "Banking",
        desc: "An emailer design language that keeps banking communication clear and on-brand.",
        bg: "bg-gradient-to-br from-lav-100 via-lav-200 to-white", cover: "/assets/covers/bandhan-bank.jpg",
        page: {
            heading: "Bandhan Bank",
            banner: "/assets/covers/bandhan-bank.jpg",
            meta: buildMeta("Visual / Graphic Designer", "Emailer Design", CREATIVE_DELIVERABLES),
            sections: creativeSections(),
        },
    },
    { slug: "revolgo", title: "Revolgo", format: "Landing Page", industry: "Web Design", desc: "A conversion-focused landing page with sharp messaging and confident visuals.", bg: "bg-gradient-to-br from-sun-soft/30 via-lav-100 to-lav-200" },
];
